<template>
  <div v-if="isModalOpen"
    class="fixed flex items-center justify-center px-0 inset-0 z-50 bg-[#00000080] pointer-events-none" @wheel.prevent
    @touchmove.prevent.passive="false">
    <div
      class="relative flex items-center justify-center mx-[10px] pointer-events-auto modal-container mobile:w-[400px] mobile:h-[70%] tablet:w-[700px] tablet:h-[70%] laptop:h-[550px] laptop:w-[800px] largeDesktop:w-[1000px] largeDesktop:h-[700px] overflow-hidden rounded-[20px] border-[5px] border-white bg-white pointer-events-auto">
      <!-- Close Button -->
      <button class="absolute top-4 right-4 w-[35px] h-[35px] p-1.5 rounded-full bg-[#00000033] text-orange-500 z-50"
        @click="closeBtn">
        <img src="../../assets/images/modal-cross-icon.png" alt="" class="w-full h-full" />
      </button>

      <!-- Image -->
      <img v-if="images.length" ref="imageEl" :src="images[currentIndex].url" :style="{
        transform: `translate(${translateX}px, ${translateY}px) scale(${zoom})`,
        cursor: isDragging ? 'grabbing' : zoom > defaultZoom ? 'grab' : 'default',
        transition: isImageLoaded ? 'transform 100ms linear' : 'none',
        'max-width': '100%',
        'max-height': '100%',
        'width': 'auto',
        'height': 'auto'
      }" class="select-none object-contain rounded-[20px]" @load="onImageLoad" @wheel.prevent="handleWheel"
        @mousedown="startDrag" @touchstart="startTouch" @touchmove="onTouchMove" @touchend="endTouch"
        draggable="false" />


      <!-- Navigation -->
      <div v-if="images.length > 1"
        class="absolute bottom-5 left-1/2 -translate-x-1/2 flex items-center gap-3 px-[3px] py-1 rounded-full bg-black-100 z-50">
        <button class="w-8 h-8 text-2xl flex items-center justify-center rounded-full text-white hover:bg-[#FE5000]"
          @click="prevImage">
          <i class="fa fa-angle-left"></i>
        </button>
        <button class="w-8 h-8 text-2xl flex items-center justify-center rounded-full text-white hover:bg-[#FE5000]"
          @click="nextImage">
          <i class="fa fa-angle-right"></i>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { disableScroll, enableScroll } from '../../helpers/scrollHelper';

export default {
  props: ["images", "currentIndex", "isModalOpen"],
  data() {
    return {
      zoom: 5,
      defaultZoom: 1,
      translateX: 0,
      translateY: 0,
      isDragging: false,
      lastX: 0,
      lastY: 0,
      isImageLoaded: false,
      startDistance: null,
      startZoom: 1,
    };
  },
  watch: {
    isModalOpen(val) {
    if (val) {
      // Disable pinch zoom globally (temporarily)
      document.documentElement.style.touchAction = "none";

      document.addEventListener("gesturestart", this.preventGesture, { passive: false });
      document.addEventListener("gesturechange", this.preventGesture, { passive: false });
      document.addEventListener("gestureend", this.preventGesture, { passive: false });

      document.documentElement.style.overflow = "hidden";
      disableScroll();

      this.$nextTick(() => {
        if (this.$refs.imageEl) {
          this.fitToScreen();
        }
      });
    } else {
      // Restore normal behaviour
      document.documentElement.style.touchAction = "";

      document.removeEventListener("gesturestart", this.preventGesture);
      document.removeEventListener("gesturechange", this.preventGesture);
      document.removeEventListener("gestureend", this.preventGesture);

      document.documentElement.style.overflow = "";
      enableScroll();
    }
  }
  },
  mounted() {
    // disable browser pinch zoom
    document.addEventListener("gesturestart", (e) => e.preventDefault());
    document.addEventListener("gesturechange", (e) => e.preventDefault());
    document.addEventListener("gestureend", (e) => e.preventDefault());
  },
  methods: {
    onImageLoad() {
      this.isImageLoaded = true;
      this.setDefaultScale();
    },

    setDefaultScale() {
      const width = window.innerWidth;

      if (width > 1440) {
        this.zoom = 1;
      } else if (width > 640) {
        this.zoom = 1;
      } else {
        this.zoom = 1;
      }

      this.defaultZoom = 1;
      this.translateX = 0;
      this.translateY = 0;
    },
    fitToScreen() {
      const img = this.$refs.imageEl;
      if (!img) return;

      const modal = img.parentElement;
      const modalW = modal.clientWidth;
      const modalH = modal.clientHeight;
      const naturalW = img.naturalWidth;
      const naturalH = img.naturalHeight;

      // Aspect ratio ke sath fit karna
      const scale = Math.min(modalW / naturalW, modalH / naturalH);

      this.defaultZoom = scale;
      this.zoom = scale;
      this.translateX = 0;
      this.translateY = 0;
    },

    resetZoom() {
      this.zoom = this.defaultZoom;
      this.translateX = 0;
      this.translateY = 0;
    },
    handleWheel(e) {
      if (!this.isModalOpen) return;
      const delta = e.deltaY > 0 ? -0.1 : 0.1;
      this.zoom = Math.min(Math.max(this.zoom + delta, this.defaultZoom), 8);
    },
    startDrag(e) {
      if (this.zoom === this.defaultZoom) return;
      this.isDragging = true;
      this.lastX = e.clientX;
      this.lastY = e.clientY;
      document.addEventListener("mousemove", this.onDrag);
      document.addEventListener("mouseup", this.stopDrag);
    },
    onDrag(e) {
      if (!this.isDragging) return;
      const dx = e.clientX - this.lastX;
      const dy = e.clientY - this.lastY;

      this.translateX += dx;
      this.translateY += dy;

      this.lastX = e.clientX;
      this.lastY = e.clientY;
    },
    stopDrag() {
      this.isDragging = false;
      document.removeEventListener("mousemove", this.onDrag);
      document.removeEventListener("mouseup", this.stopDrag);
    },
    // mobile touch handlers
    startTouch(e) {
      if (e.touches.length === 2) {
        const dx = e.touches[0].clientX - e.touches[1].clientX;
        const dy = e.touches[0].clientY - e.touches[1].clientY;
        this.startDistance = Math.sqrt(dx * dx + dy * dy);
        this.startZoom = this.zoom;
      } else if (e.touches.length === 1 && this.zoom > this.defaultZoom) {
        this.isDragging = true;
        this.lastX = e.touches[0].clientX;
        this.lastY = e.touches[0].clientY;
      }
    },
    onTouchMove(e) {
      if (e.touches.length === 2 && this.startDistance) {
        const dx = e.touches[0].clientX - e.touches[1].clientX;
        const dy = e.touches[0].clientY - e.touches[1].clientY;
        const newDistance = Math.sqrt(dx * dx + dy * dy);

        const scaleChange = newDistance / this.startDistance;
        this.zoom = Math.min(
          Math.max(this.startZoom * scaleChange, this.defaultZoom),
          8
        );
      } else if (e.touches.length === 1 && this.isDragging) {
        const dx = e.touches[0].clientX - this.lastX;
        const dy = e.touches[0].clientY - this.lastY;

        this.translateX += dx;
        this.translateY += dy;

        this.lastX = e.touches[0].clientX;
        this.lastY = e.touches[0].clientY;
      }
    },
    endTouch() {
      this.isDragging = false;
      this.startDistance = null;
    },
    nextImage() {
      this.$emit(
        "update:currentIndex",
        (this.currentIndex + 1) % this.images.length
      );
      this.$nextTick(() => {
        this.fitToScreen();
        this.resetZoom();
      });
    },
    prevImage() {
      this.$emit(
        "update:currentIndex",
        (this.currentIndex - 1 + this.images.length) % this.images.length
      );
      this.$nextTick(() => {
        this.fitToScreen();
        this.resetZoom();
      });
    },
    closeBtn() {
      document.documentElement.style.overflow = "";
      enableScroll()
      this.$emit("close");
    },
  },
};
</script>
<style scoped>
.modal-container {
  touch-action: none; /* disable browser default pinch zoom */
}
</style>